import React, { useState } from 'react';
import {
Box,
Button,
Card,
CardHeader,
CardContent,
CardActions,
Typography,
useMediaQuery,
useTheme,
Divider,
Chip,
IconButton,
Tooltip,
Dialog,
DialogTitle,
DialogContent
} from '@mui/material';
import {
School as SchoolIcon,
Edit as EditIcone,
Category as CategoryIcon,
Visibility as VisibilityIcon,
AttachFile as AttachFileIcon,
Article as ArticleIcon,
Delete as DeleteIcon,
Close
} from '@mui/icons-material';
import { BASE_URL } from '../../utilities/Env';

const ResearchCard = ({ research, isUserOnlyMode, onDelete, onEditDetails, onViewDetails, getResearcherName, getUniversityName, getAreaName }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
    const [selectedPdfUrl, setSelectedPdfUrl] = useState('');

    const handlePdfView = (research) => {
        if (research.fileName) {
            const pdfUrl = `${BASE_URL}${research.fileName}`;
            setSelectedPdfUrl(pdfUrl);
            setPdfDialogOpen(true);
        }
    };

    const handleClosePdfDialog = () => {
        setPdfDialogOpen(false);
        setSelectedPdfUrl('');
    };

    return (
        <>
            <Card sx={{
                boxShadow: 3, borderRadius: 8,
                marginTop: 2,
                transition: 'transform 0.3s, box-shadow 0.3s',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 6
                }
            }}>
                <CardHeader
                    title={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <SchoolIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold' }}>
                                {getResearcherName(research.researcherName)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                at
                            </Typography>
                            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold' }}>
                                {getUniversityName(research.universityName)}
                            </Typography>
                        </Box>
                    }
                    action={
                        <>
                            {isUserOnlyMode && (
                                <>
                                    <Tooltip title="Delete Your Research">
                                        <IconButton
                                            color="error"
                                            onClick={() => onDelete(research)}
                                            sx={{
                                                backgroundColor: theme.palette.secondary.main,
                                                marginRight: 2,
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.success.main,
                                                }
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Edit Your Research Details">
                                        <IconButton
                                            color="secondary"
                                            onClick={() => onEditDetails(research)}
                                            sx={{
                                                backgroundColor: theme.palette.success.main,
                                                marginRight: 2,
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.secondary.main,
                                                }
                                            }}
                                        >
                                            <EditIcone />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                            {research.researchStatus !== "Work In Progress" && (
                                <>
                                    <Tooltip title="View uploaded file">
                                        <IconButton
                                            color="primary"
                                            onClick={() => handlePdfView(research)}
                                            sx={{
                                                backgroundColor: theme.palette.primary.main,
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.success.main,
                                                }
                                            }}
                                        >
                                            <AttachFileIcon />
                                        </IconButton>
                                    </Tooltip>&nbsp;&nbsp;

                                    <Tooltip title="View full research details">
                                        <IconButton
                                            color="primary"
                                            onClick={() => onViewDetails(research)}
                                            sx={{
                                                backgroundColor: theme.palette.primary.main,
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.success.main,
                                                }
                                            }}
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                        </>
                    }
                    sx={{ pb: 1 }}
                />

                <Divider />

                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    {/* Research Topic */}
                    <Typography 
                        variant="h6" 
                        component="h6" 
                        sx={{ 
                            mb: 2,
                            color: theme.palette.text.primary,
                            lineHeight: 1.3
                        }}
                    >
                        {research.researchTopic}
                    </Typography>
                </CardContent>

                <Divider />

                <CardActions sx={{
                    p: 2,
                    pt: 1.5,
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: 1,
                    flexWrap: 'wrap'
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        flexGrow: 1
                    }}>
                        <Chip 
                            icon={<ArticleIcon sx={{ fontSize: 16 }} />}
                            label={research.researchStatus} 
                            color={research.researchStatus === "Completed" ? "success" : "warning"} 
                            size="small" 
                            variant="filled"
                        />

                        <Chip
                            icon={<CategoryIcon sx={{ fontSize: 16 }} />}
                            label={getAreaName(research.researchArea)} 
                            color="primary"
                            size="small"
                            variant="outlined"
                        />

                        <Chip
                            label={research.researchType}
                            size="small"
                            sx={{ 
                                backgroundColor: theme.palette.grey[100],
                                color: theme.palette.grey[700],
                                border: `1px solid ${theme.palette.grey[300]}`
                            }}
                        />
                    </Box>

                    <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                            fontSize: '0.75rem',
                            fontWeight: 'medium'
                        }}
                    >
                        📅 {research.uploadDate}
                    </Typography>
                </CardActions>
            </Card>

            {/* PDF Viewer Dialog */}
            <Dialog
                open={pdfDialogOpen}
                onClose={handleClosePdfDialog}
                maxWidth="lg"
                fullWidth
                sx={{ '& .MuiDialog-paper': { minHeight: '80vh' } }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            {research.researchTopic} - PDF Document
                        </Typography>
                        <IconButton onClick={handleClosePdfDialog}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, minHeight: '70vh' }}>
                    {selectedPdfUrl && (
                        <iframe
                            src={selectedPdfUrl}
                            width="100%"
                            height="100%"
                            style={{ border: 'none', minHeight: '70vh' }}
                            title={`PDF Document - ${research.researchTopic}`}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ResearchCard;